/**
 * HRLifecycle — the employment-lifecycle surface: every worker's employment
 * history (table 0092) with the three transitions (close / correct / rehire),
 * and per closed employment the P45 arc (issue → view → send, tables
 * 0093/0094) plus the P60 placeholder. Clone of HRTickets' list → drawer →
 * action shape, which is itself the FinanceInvoices shape.
 *
 * TWO ID SPACES, kept distinct on purpose: close and rehire address the
 * PERSON (employee id — "this person is leaving / returning"); correct and
 * the P45 address the employment VERSION (employment id — a rehired person
 * has several, each due its own certificate). The employments history read is
 * where an employment id becomes discoverable at all.
 *
 * The heavy actions carry the typed-word gate idiom (PayslipGate /
 * PayrollCorrectionPanel): each gate is its OWN instance with its own word —
 * "close" (a leaving is one-shot; wrong ones are corrected, not re-closed),
 * "supersede" (a correction replaces the version, never rewrites it) and
 * "issue" (a statutory document with no edit and no delete). Rehire is a
 * plain form: it creates an OPEN row beside history, nothing irreversible.
 *
 * Amber is the sensitivity signal and appears NOWHERE on this page — status
 * renders as the quiet dot pairs (active = sage, leaver/closed = grey).
 * Refusals render VERBATIM via apiErrorMessage: the server names the fact at
 * fault, and paraphrasing would collapse distinct legal situations into one.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Eye, FileText, Send, X } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import {
  useAsync, usePolledAsync,
  fetchDirectory, fetchEmployments,
  closeEmployment, correctEmployment, rehireEmployee,
  fetchP45s, previewP45, issueP45, fetchP45Document, sendP45, fetchP45SendStatus,
} from '../../api'
import type {
  ApiEmployeeDirectoryRow, EmploymentHistoryRow, P45, P45Preview, P45Document, P45SendStatus,
} from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { ink, neutral, overlay, statusTicket } from '../../../../theme/tokens'

// ── shared bits ───────────────────────────────────────────────────────────────

const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'
const inputCls = 'h-9 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'
const quietBtnCls = 'k-btn-secondary k-btn-secondary--flex disabled:opacity-50'

// Employee status → quiet dot + label (the HREmployees idiom): active rides
// the resolved sage pair, leaver the closed grey. Never amber.
const EMPLOYEE_STATUS: Record<string, { dot: string; label: string }> = {
  active: statusTicket.resolved,
  leaver: statusTicket.closed,
}

function EmployeeStatus({ status }: { status: string }) {
  const cfg = EMPLOYEE_STATUS[status] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium capitalize whitespace-nowrap" style={{ color: cfg.label }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      {status}
    </span>
  )
}

// The TWO orthogonal employment facts, each its own chip — never one label.
// status says whether the person left (leavingDate); current says whether
// this VERSION is the live one (supersededBy). A superseded closed row is
// "closed" AND "superseded" side by side.
function EmploymentFacts({ row }: { row: EmploymentHistoryRow }) {
  const statusCfg = row.status === 'open' ? statusTicket.resolved : statusTicket.closed
  return (
    <span className="inline-flex items-center gap-3">
      <span className="inline-flex items-center gap-1.5 text-12 font-medium capitalize" style={{ color: statusCfg.label }}>
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusCfg.dot }} />
        {row.status}
      </span>
      <span className="inline-flex items-center px-2 py-0.5 rounded text-11 font-medium bg-surface-panel"
        style={{ color: row.current ? ink.secondary : ink.faint }}
        data-testid={`employment-currency-${row.id}`}>
        {row.current ? 'current' : 'superseded'}
      </span>
    </span>
  )
}

function fmtDay(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtStamp(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** base64 → a blob URL (the PayslipGate helper verbatim: the preview arm is
 *  JSON, so its bytes arrive base64; the STORED document is fetched as bytes
 *  from its own route). */
function pdfObjectUrl(base64: string): string {
  const bin = atob(base64)
  const buf = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i)
  return URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
}

// ── the CLOSE gate (employee-keyed; word "close") ─────────────────────────────

export function CloseEmploymentGate({ employee, onDone, onCancel }: {
  employee: ApiEmployeeDirectoryRow
  onDone: () => void
  onCancel: () => void
}) {
  const CONFIRM_WORD = 'close'
  const [leavingDate, setLeavingDate] = useState('')
  const [changeReason, setChangeReason] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Target change invalidates the typed intent — render-time (the React
  // reset-on-prop recipe), never an effect: an effect would paint the old
  // fields beside the new person's name first.
  const [prevID, setPrevID] = useState(employee.id)
  if (prevID !== employee.id) {
    setPrevID(employee.id)
    setLeavingDate('')
    setChangeReason('')
    setConfirmText('')
    setError(null)
  }

  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const canCommit = leavingDate !== '' && changeReason.trim() !== '' && confirmed && !busy

  const commit = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      // changedBy is NOT sent: the server stamps the session actor.
      await closeEmployment(employee.id, { leavingDate, changeReason })
      toast.success(`${employee.fullName} recorded as leaving ${fmtDay(leavingDate)}`)
      onDone()
    } catch (e) {
      // Verbatim: the named 422 (no open employment → "correct" pointer, an
      // unregistered year, a date before the start) reaches the operator
      // exactly as the server said it.
      setError(apiErrorMessage(e, 'Closing this employment failed'))
    } finally {
      setBusy(false)
    }
  }, [employee.id, employee.fullName, leavingDate, changeReason, onDone])

  return (
    <div className="flex flex-col gap-3 p-4 rounded-tile border border-edge-control bg-surface-panel" data-testid="close-gate">
      <div>
        <div className="text-13 font-semibold text-ink-primary">Record {employee.fullName} as a leaver</div>
        <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
          Closing sets the leaving date on the open employment. A close is one-shot:
          if the date turns out wrong, the close is <em>corrected</em> — superseded by a
          new version — never re-closed.
        </div>
      </div>

      {error && (
        <div role="alert" data-testid="close-refusal"
          className="text-12 leading-relaxed rounded-tile border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="close-leaving" className={labelCls}>Leaving date</label>
          <input id="close-leaving" type="date" value={leavingDate}
            onChange={e => setLeavingDate(e.target.value)} className={inputCls} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="close-reason" className={labelCls}>Change reason</label>
          <input id="close-reason" value={changeReason} autoComplete="off"
            onChange={e => setChangeReason(e.target.value)}
            placeholder="why this person is leaving" className={`${inputCls} w-full`} />
        </div>
        <div>
          <label htmlFor="close-confirm" className={labelCls}>Type “{CONFIRM_WORD}” to confirm</label>
          <input id="close-confirm" value={confirmText} autoComplete="off"
            onChange={e => setConfirmText(e.target.value)}
            placeholder={CONFIRM_WORD} className={inputCls} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={commit} disabled={!canCommit}
          className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40">
          <Check size={14} /> {busy ? 'Closing…' : 'Close employment'}
        </button>
        <button onClick={onCancel} className={quietBtnCls}>Cancel</button>
      </div>
    </div>
  )
}

// ── the CORRECT gate (employment-keyed; word "supersede") ─────────────────────

export function CorrectEmploymentGate({ employment, onDone, onCancel }: {
  employment: EmploymentHistoryRow
  onDone: () => void
  onCancel: () => void
}) {
  const CONFIRM_WORD = 'supersede'
  const [unleave, setUnleave] = useState(false)
  const [leavingDate, setLeavingDate] = useState('')
  const [changeReason, setChangeReason] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [prevID, setPrevID] = useState(employment.id)
  if (prevID !== employment.id) {
    setPrevID(employment.id)
    setUnleave(false)
    setLeavingDate('')
    setChangeReason('')
    setConfirmText('')
    setError(null)
  }

  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const canCommit = (unleave || leavingDate !== '') && changeReason.trim() !== '' && confirmed && !busy

  const commit = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      // Un-leave = the date key OMITTED (absence by omission — an empty
      // string is a malformed date the server refuses). changedBy is not
      // sent: the server stamps the session actor.
      await correctEmployment(employment.id,
        unleave ? { changeReason } : { leavingDate, changeReason })
      toast.success(unleave ? 'Close corrected — the employment is open again' : `Leaving date corrected to ${fmtDay(leavingDate)}`)
      onDone()
    } catch (e) {
      setError(apiErrorMessage(e, 'Correcting this employment failed'))
    } finally {
      setBusy(false)
    }
  }, [employment.id, unleave, leavingDate, changeReason, onDone])

  return (
    <div className="flex flex-col gap-3 p-4 rounded-control border border-edge-control bg-surface-panel" data-testid="correct-gate">
      <div>
        <div className="text-13 font-semibold text-ink-primary">Correct this close</div>
        <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
          A correction <em>supersedes</em> this version with a new one — the original
          row is never rewritten, and the history keeps both. Give the corrected
          leaving date, or mark the whole close a mistake to reopen the employment.
        </div>
      </div>

      {error && (
        <div role="alert" data-testid="correct-refusal"
          className="text-12 leading-relaxed rounded-tile border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}

      <label className="inline-flex items-center gap-2 text-12 text-ink-body">
        <input type="checkbox" checked={unleave} onChange={e => setUnleave(e.target.checked)} />
        The close itself was a mistake — remove the leaving date (un-leave)
      </label>

      <div className="flex flex-wrap items-end gap-3">
        {!unleave && (
          <div>
            <label htmlFor="correct-leaving" className={labelCls}>Corrected leaving date</label>
            <input id="correct-leaving" type="date" value={leavingDate}
              onChange={e => setLeavingDate(e.target.value)} className={inputCls} />
          </div>
        )}
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="correct-reason" className={labelCls}>Change reason</label>
          <input id="correct-reason" value={changeReason} autoComplete="off"
            onChange={e => setChangeReason(e.target.value)}
            placeholder="what was wrong" className={`${inputCls} w-full`} />
        </div>
        <div>
          <label htmlFor="correct-confirm" className={labelCls}>Type “{CONFIRM_WORD}” to confirm</label>
          <input id="correct-confirm" value={confirmText} autoComplete="off"
            onChange={e => setConfirmText(e.target.value)}
            placeholder={CONFIRM_WORD} className={inputCls} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={commit} disabled={!canCommit}
          className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40">
          <Check size={14} /> {busy ? 'Superseding…' : 'Supersede this version'}
        </button>
        <button onClick={onCancel} className={quietBtnCls}>Cancel</button>
      </div>
    </div>
  )
}

// ── the P45 ISSUE gate (employment-keyed; word "issue") ───────────────────────
// The PayslipGate discipline whole: preview first (base64 → blob → inline
// iframe, never a tab), ONE idempotency key per successful preview KEPT on
// commit failure, target change resets at render time, and after commit the
// STORED bytes are fetched back from the read route — round trip, not trust.

export function P45IssueGate({ employment, onIssued, onClose }: {
  employment: EmploymentHistoryRow
  onIssued: () => void
  onClose: () => void
}) {
  const CONFIRM_WORD = 'issue'
  const [previewed, setPreviewed] = useState<P45Preview | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stored, setStored] = useState<P45Document | null>(null)

  const [prevID, setPrevID] = useState(employment.id)
  if (prevID !== employment.id) {
    setPrevID(employment.id)
    setPreviewed(null)
    setIdempotencyKey(null)
    setConfirmText('')
    setError(null)
    setStored(null)
    setPreviewUrl(null)
  }

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])
  useEffect(() => () => { if (stored) URL.revokeObjectURL(stored.blobUrl) }, [stored])

  const runPreview = useCallback(async () => {
    setBusy('preview')
    setError(null)
    try {
      const p = await previewP45(employment.id)
      setPreviewed(p)
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return pdfObjectUrl(p.document) })
      setIdempotencyKey(crypto.randomUUID())
      setConfirmText('')
    } catch (e) {
      // The refusal the commit would hit, by name: still-open 422, superseded
      // 409, already-issued 409, no-figures 422 — rendered verbatim.
      setPreviewed(null)
      setIdempotencyKey(null)
      setError(apiErrorMessage(e, 'Previewing this P45 failed'))
    } finally {
      setBusy(null)
    }
  }, [employment.id])

  const commit = useCallback(async () => {
    if (!idempotencyKey) return
    setBusy('commit')
    setError(null)
    try {
      const { preview } = await issueP45(employment.id, idempotencyKey)
      const doc = await fetchP45Document(preview.p45.id)
      setStored(prev => { if (prev) URL.revokeObjectURL(prev.blobUrl); return doc })
      onIssued()
    } catch (e) {
      // The key is KEPT: the retry must reach the SAME write.
      setError(apiErrorMessage(e, 'Issuing this P45 failed'))
    } finally {
      setBusy(null)
    }
  }, [employment.id, idempotencyKey, onIssued])

  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const canCommit = previewed !== null && idempotencyKey !== null && confirmed && busy === null

  return (
    <div className="flex flex-col gap-3 p-4 rounded-control border border-edge-control bg-surface-panel" data-testid="p45-issue-gate">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-13 font-semibold text-ink-primary">Issue P45</div>
          <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
            For the employment ending {fmtDay(employment.leavingDate)}. Preview first:
            the certificate below is exactly what will be stored. A P45 cannot be
            edited or deleted once issued.
          </div>
        </div>
        <button onClick={onClose} aria-label="Close issue gate"
          className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
          <X size={16} />
        </button>
      </div>

      {error && (
        <div role="alert" data-testid="p45-refusal"
          className="text-12 leading-relaxed rounded-tile border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}

      {stored ? (
        <div className="flex flex-col gap-2" data-testid="p45-issued">
          <div className="text-12 font-medium text-ink-primary">
            Issued. The document below is the stored certificate, fetched back from the record.
          </div>
          <div className="text-11 text-ink-muted tabular-nums break-all">sha256 {stored.digestSha256}</div>
          <iframe title="Issued P45" src={stored.blobUrl}
            className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
        </div>
      ) : (
        <>
          {previewUrl && (
            <div className="flex flex-col gap-2">
              <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
                Preview — not yet issued
              </div>
              <iframe title="P45 preview" src={previewUrl} data-testid="p45-preview-frame"
                className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <button onClick={runPreview} disabled={busy !== null} className={quietBtnCls}>
              <Eye size={14} /> {busy === 'preview' ? 'Previewing…' : previewed ? 'Preview again' : 'Preview'}
            </button>

            {previewed && (
              <div>
                <label htmlFor="p45-confirm" className={labelCls}>Type “{CONFIRM_WORD}” to confirm</label>
                <input id="p45-confirm" value={confirmText} autoComplete="off"
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD} className={inputCls} />
              </div>
            )}

            <button onClick={commit} disabled={!canCommit}
              className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40">
              <Check size={14} /> {busy === 'commit' ? 'Issuing…' : 'Issue P45'}
            </button>

            {!previewed && <span className="text-12 text-ink-muted">Preview before issuing.</span>}
          </div>
        </>
      )}
    </div>
  )
}

// ── send state (driven entirely by the send-status read) ──────────────────────
// "none" is a 200 STATE — not yet sent, offer the Send button. sentAt exists
// only on a confirmed receipt, so the "sent … delivered" line is always
// truthful. failed/unknown are terminal receipt states reported honestly; the
// receipt is one-per-certificate, so no re-send is offered on them.

export function P45SendState({ p45 }: { p45: P45 }) {
  const { data: status, error, refresh } = usePolledAsync<P45SendStatus>(
    () => fetchP45SendStatus(p45.id), 10_000, [p45.id])
  const [sending, setSending] = useState(false)

  const doSend = useCallback(async () => {
    setSending(true)
    try {
      await sendP45(p45.id) // 202: a send is OWED and recorded, not delivered
      toast.success('P45 send queued')
      refresh()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Sending this P45 failed'))
    } finally {
      setSending(false)
    }
  }, [p45.id, refresh])

  if (error) return <div className="text-12 text-ink-muted" data-testid="p45-send-state">Send state unavailable: {error}</div>
  if (!status) return <div className="text-12 text-ink-faint" data-testid="p45-send-state">Checking send state…</div>

  return (
    <div className="text-12 text-ink-body" data-testid="p45-send-state">
      {status.status === 'none' && (
        <span className="inline-flex items-center gap-3">
          <span className="text-ink-muted">Not yet sent.</span>
          <button onClick={doSend} disabled={sending} className={quietBtnCls} data-testid="p45-send-button">
            <Send size={12} /> {sending ? 'Queuing…' : 'Send to leaver'}
          </button>
        </span>
      )}
      {status.status === 'pending' && <span>Sending…</span>}
      {status.status === 'confirmed' && (
        <span>Sent to {status.recipientEmail} on {fmtStamp(status.sentAt)} — delivered.</span>
      )}
      {status.status === 'failed' && (
        <span>Send failed after {status.attempts} attempt{status.attempts !== 1 ? 's' : ''} — the certificate is unsent; see the orchestrator.</span>
      )}
      {status.status === 'unknown' && (
        <span>Delivery unknown after {status.attempts} attempt{status.attempts !== 1 ? 's' : ''} — an earlier attempt may have delivered.</span>
      )}
    </div>
  )
}

// ── the per-closed-employment P45 section ─────────────────────────────────────

function P45Section({ employment }: { employment: EmploymentHistoryRow }) {
  const [reload, setReload] = useState(0)
  const { data: p45s, loading, error } = useAsync<P45[]>(
    () => fetchP45s({ employmentId: employment.id }), [employment.id, reload])
  const [issuing, setIssuing] = useState(false)
  const [doc, setDoc] = useState<P45Document | null>(null)
  const [docBusy, setDocBusy] = useState(false)

  useEffect(() => () => { if (doc) URL.revokeObjectURL(doc.blobUrl) }, [doc])

  const view = useCallback(async (id: string) => {
    setDocBusy(true)
    try {
      const d = await fetchP45Document(id)
      setDoc(prev => { if (prev) URL.revokeObjectURL(prev.blobUrl); return d })
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Fetching the stored P45 failed'))
    } finally {
      setDocBusy(false)
    }
  }, [])

  if (loading) return <div className="text-12 text-ink-faint">Checking for a P45…</div>
  if (error) return <div className="text-12 text-ink-muted">P45 state unavailable: {error}</div>

  const p45 = p45s && p45s.length > 0 ? p45s[0] : null

  if (!p45) {
    // ABSENCE, honestly: no certificate exists for this version. Only the
    // CURRENT version can earn one (the backend refuses a superseded version
    // with a named 409; withholding the button is defence in depth, not the
    // enforcement — the PayslipCell discipline).
    if (!employment.current) {
      return <div className="text-12 text-ink-faint">No P45 was issued for this superseded version.</div>
    }
    return issuing ? (
      <P45IssueGate employment={employment}
        onIssued={() => setReload(n => n + 1)}
        onClose={() => setIssuing(false)} />
    ) : (
      <button onClick={() => setIssuing(true)} className={quietBtnCls} data-testid={`p45-issue-open-${employment.id}`}>
        <FileText size={12} /> Issue P45
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`p45-row-${employment.id}`}>
      <div className="flex items-center gap-3 flex-wrap text-12">
        <span className="inline-flex items-center gap-1.5 font-medium text-ink-primary">
          <FileText size={12} /> P45 issued {fmtDay(p45.issuedAt)}
        </span>
        <span className="text-ink-faint tabular-nums" title={p45.digestSha256}>
          sha256 {p45.digestSha256.slice(0, 12)}…
        </span>
        <button onClick={() => view(p45.id)} disabled={docBusy} className={quietBtnCls}>
          <Eye size={12} /> {docBusy ? 'Fetching…' : 'View'}
        </button>
      </div>
      <P45SendState p45={p45} />
      {doc && (
        <div className="flex flex-col gap-1">
          <div className="text-11 text-ink-muted tabular-nums break-all">stored sha256 {doc.digestSha256}</div>
          <iframe title={`P45 ${p45.taxYear}`} src={doc.blobUrl}
            className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
        </div>
      )}
    </div>
  )
}

// ── rehire (employee-keyed; a plain form — it creates an OPEN row) ────────────

function RehireForm({ employee, onDone }: { employee: ApiEmployeeDirectoryRow; onDone: () => void }) {
  const [startDate, setStartDate] = useState('')
  const [changeReason, setChangeReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const commit = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await rehireEmployee(employee.id, { startDate, changeReason })
      toast.success(`${employee.fullName} rehired from ${fmtDay(startDate)}`)
      onDone()
    } catch (e) {
      // The named 409 while an open employment exists ("close it first")
      // renders verbatim.
      setError(apiErrorMessage(e, 'Rehiring failed'))
    } finally {
      setBusy(false)
    }
  }, [employee.id, employee.fullName, startDate, changeReason, onDone])

  return (
    <div className="flex flex-col gap-3 p-4 rounded-tile border border-edge-control bg-surface-panel" data-testid="rehire-form">
      <div className="text-13 font-semibold text-ink-primary">Rehire {employee.fullName}</div>
      {error && (
        <div role="alert" data-testid="rehire-refusal"
          className="text-12 leading-relaxed rounded-tile border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="rehire-start" className={labelCls}>Start date</label>
          <input id="rehire-start" type="date" value={startDate}
            onChange={e => setStartDate(e.target.value)} className={inputCls} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="rehire-reason" className={labelCls}>Change reason</label>
          <input id="rehire-reason" value={changeReason} autoComplete="off"
            onChange={e => setChangeReason(e.target.value)}
            placeholder="why this person is returning" className={`${inputCls} w-full`} />
        </div>
        <button onClick={commit} disabled={busy || startDate === '' || changeReason.trim() === ''}
          className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40">
          <Check size={14} /> {busy ? 'Rehiring…' : 'Open new employment'}
        </button>
      </div>
    </div>
  )
}

// ── the drawer ────────────────────────────────────────────────────────────────

function LifecycleDrawer({ employee, onClose, onChanged }: {
  employee: ApiEmployeeDirectoryRow
  onClose: () => void
  onChanged: () => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [reload, setReload] = useState(0)
  const { data: history, loading, error } = useAsync<EmploymentHistoryRow[]>(
    () => fetchEmployments(employee.id), [employee.id, reload])
  const [action, setAction] = useState<'close' | 'rehire' | null>(null)
  const [correcting, setCorrecting] = useState<string | null>(null) // employment id

  const changed = useCallback(() => {
    setAction(null)
    setCorrecting(null)
    setReload(n => n + 1)
    onChanged()
  }, [onChanged])

  // Oldest-first: the history reads as a career, top to bottom.
  const rows = [...(history ?? [])].sort((a, b) =>
    a.startDate.localeCompare(b.startDate) || a.createdAt.localeCompare(b.createdAt))
  const hasOpen = rows.some(r => r.status === 'open' && r.current)

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer" data-testid="lifecycle-drawer"
        style={{ width: 'min(640px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="text-detail-title font-bold text-ink-primary truncate">{employee.fullName}</div>
            <div className="text-12 text-ink-muted">{employee.employeeNumber} · <EmployeeStatus status={employee.status} /></div>
          </div>
          <button onClick={onClose} aria-label="Close drawer"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-5">
          <div>
            <div className="k-eyebrow uppercase mb-2">Employment history</div>
            {loading && <div className="text-12 text-ink-faint">Loading history…</div>}
            {error && <div className="text-12 text-ink-muted" role="alert">{error}</div>}
            {!loading && !error && rows.length === 0 && (
              <div className="text-12 text-ink-muted">No employment rows.</div>
            )}
            <div className="flex flex-col gap-3">
              {rows.map(r => (
                <div key={r.id} className="flex flex-col gap-2 p-3 rounded-tile border border-edge-row" data-testid={`employment-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-13 font-medium text-ink-primary tabular-nums">
                      {fmtDay(r.startDate)} → {r.leavingDate ? fmtDay(r.leavingDate) : 'open'}
                    </span>
                    <EmploymentFacts row={r} />
                  </div>
                  <div className="text-11 text-ink-faint">
                    {r.changedBy} · {r.changeReason} · {fmtStamp(r.createdAt)}
                  </div>

                  {r.status === 'closed' && (
                    correcting === r.id ? (
                      <CorrectEmploymentGate employment={r} onDone={changed} onCancel={() => setCorrecting(null)} />
                    ) : (
                      <div className="flex flex-col gap-2">
                        <P45Section employment={r} />
                        {r.current && (
                          <div>
                            <button onClick={() => setCorrecting(r.id)} className={quietBtnCls}>
                              Correct this close
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="k-eyebrow uppercase mb-2">Transitions</div>
            {action === 'close' && <CloseEmploymentGate employee={employee} onDone={changed} onCancel={() => setAction(null)} />}
            {action === 'rehire' && <RehireForm employee={employee} onDone={changed} />}
            {action === null && (
              <div className="flex items-center gap-2">
                {hasOpen ? (
                  <button onClick={() => setAction('close')} className={quietBtnCls} data-testid="open-close-gate">
                    Record as leaver…
                  </button>
                ) : (
                  <button onClick={() => setAction('rehire')} className={quietBtnCls} data-testid="open-rehire-form">
                    Rehire…
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="k-eyebrow uppercase mb-2">P60 — End of Year Certificate</div>
            {/* Present so the capability is visible; disabled because the law
                gates it: a P60 certifies a COMPLETED tax year, and 2026-27
                ends 5 April 2027. No P60 call is wired here. */}
            <div className="flex items-center gap-3 flex-wrap">
              <button disabled className={quietBtnCls} data-testid="p60-disabled">
                <FileText size={12} /> Issue P60
              </button>
              <span className="text-12 text-ink-muted">
                Available from 6 April 2027 — a P60 can only be issued after the tax year ends.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── main page ─────────────────────────────────────────────────────────────────

const FILTERS = ['all', 'active', 'leaver'] as const

export default function HRLifecycle() {
  const { data, loading, error, refresh } = usePolledAsync<ApiEmployeeDirectoryRow[]>(
    () => fetchDirectory(), 30_000, [])
  const [statusFilter, setStatusFilter] = useState<(typeof FILTERS)[number]>('all')
  const [detail, setDetail] = useState<ApiEmployeeDirectoryRow | null>(null)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const employees = data ?? []
  const filtered = employees.filter(e => statusFilter === 'all' || e.status === statusFilter)
  const leaverCount = employees.filter(e => e.status === 'leaver').length

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Lifecycle"
        subtitle={`${employees.length} employee${employees.length !== 1 ? 's' : ''} · ${leaverCount} leaver${leaverCount !== 1 ? 's' : ''}`}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState
            message="No employees here"
            hint={statusFilter === 'all'
              ? 'Employees appear once records exist.'
              : 'Nothing matches this status filter.'}
            actionLabel={statusFilter !== 'all' ? 'Show all' : undefined}
            onAction={() => setStatusFilter('all')}
          />
        ) : (
          <div>
            {filtered.map(e => (
              <div key={e.id} onClick={() => setDetail(e)}
                className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors"
                data-testid={`lifecycle-row-${e.id}`}>
                <span className="w-[88px] flex-shrink-0 text-12 text-ink-secondary truncate tabular-nums">{e.employeeNumber}</span>
                <span className="flex-1 min-w-0 text-13 font-medium text-ink-primary truncate">{e.fullName}</span>
                <span className="w-28 flex-shrink-0 text-12 text-ink-muted tabular-nums">{fmtDay(e.startDate)}</span>
                <span className="w-24 flex-shrink-0"><EmployeeStatus status={e.status} /></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <LifecycleDrawer
          employee={detail}
          onClose={() => setDetail(null)}
          onChanged={refresh}
        />
      )}
    </div>
  )
}
